// Zod DTOs for `GET /v1/analytics/overview` (P3-19, ADMIN-only read-only
// projection powering the Admin Overview dashboard — spec §6.5 canonical
// error codes, ADR-001 "ADMIN owns ... billing"). Single source of truth for
// `apps/api`'s route handler + the `@hono/zod-openapi` document generator,
// mirroring `./teacher-dashboard.ts`'s header convention for this package.
//
// `StatCard`: money-flavoured cards carry an integer `valueMinor` (minor
// units — cents/paisa, never a float) + ISO-4217 `currency`; `value` carries
// the human-facing number for every card (a plain count for non-money cards,
// the same magnitude as `valueMinor` — e.g. dollars — for money cards, so a
// naive renderer that only reads `value` still shows something sane).
// `delta` is an optional period-over-period change (same unit as `value`).
// Phase-3 populates counts-only cards (roster/enrollment, via
// `../services/roster.ts`'s tables) — money cards stay unpopulated until the
// full revenue series lands in P6-01 (see this ticket's Notes).
//
// `AttentionItem`: `code` is a canonical, machine-stable key (snake_case,
// stable across releases — a UI or alerting rule can key off it); `href`
// points at the admin screen that resolves it (`/admin/users` for every
// roster/enrollment item in this phase — no dedicated screen per code yet).
import { z } from 'zod';

// ─── GET /v1/analytics/overview ─────────────────────────────────────────

export const statCardSchema = z.object({
  /** Canonical, stable machine key (e.g. `"totalStudents"`). */
  key: z.string().min(1),
  /** Human-facing label for the card. */
  label: z.string().min(1),
  /** The number to render — a count, or the money card's major-unit value. */
  value: z.number(),
  /** Money cards only: integer minor-unit amount (never a float). */
  valueMinor: z.number().int().optional(),
  /** Money cards only: ISO-4217 currency code, required alongside `valueMinor`. */
  currency: z.string().length(3).optional(),
  /** Optional period-over-period change, same unit as `value`. */
  delta: z.number().optional(),
});
export type StatCard = z.infer<typeof statCardSchema>;

export const attentionItemSchema = z.object({
  /** Canonical machine key, stable across releases (e.g. `"expired_enrollments"`). */
  code: z.string().min(1),
  /** Human-facing label for the item. */
  label: z.string().min(1),
  /** How many records need attention for this code. */
  count: z.number().int().nonnegative(),
  /** Admin-app relative path that resolves this item, if one exists. */
  href: z.string().optional(),
});
export type AttentionItem = z.infer<typeof attentionItemSchema>;

export const adminOverviewResponseSchema = z.object({
  /** ISO timestamp of when this projection was computed — never cached
   * across requests, always "now" at request time. */
  generatedAt: z.string().datetime(),
  stats: z.array(statCardSchema),
  needsAttention: z.array(attentionItemSchema),
});
export type AdminOverviewResponse = z.infer<typeof adminOverviewResponseSchema>;
